import * as React from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import styled from 'styled-components';

import { translate } from '@estee/elc-service';

import { classes } from '../../internal/utils/Classes';
import { ACCEPTABLE_KEYS_PRESSED, handleKeyPress } from '../../internal/utils/KeyPressHandler';
import { SpinnerContainer } from '../../internal/views/common/SpinnerContainer';
import { ProductReviewsViewController } from '../../internal/controllers/ProductReviewsViewController';
import { REVIEWS, REVIEWS_WRAPPER } from '../../internal/constants/DataTestIds';

export interface ITranslation {
    reviewsTabLabel: string;
    askAnswerTabsLabel: string;
}

export interface IProductReviewsConfig {
    className?: string;
    productId: string;
    reviewsPaginationType: string;
    prDisclosureCode?: string;
}

export interface IProductReviews {
    translations?: ITranslation;
    config: IProductReviewsConfig;
    viewController: ProductReviewsViewController;
}

const TabbedContent = styled.div`
    display: none;
    &.tabs-content-active {
        display: block;
    }
`;

const acceptedKeys = [ACCEPTABLE_KEYS_PRESSED.ENTER, ACCEPTABLE_KEYS_PRESSED.SPACEBAR];

@translate(['reviewsTabLabel', 'askAnswerTabsLabel'])
@observer
export class ProductReviews extends React.Component<IProductReviews> {
    private productReviewsViewController: ProductReviewsViewController;

    constructor(props: IProductReviews) {
        super(props);
        this.productReviewsViewController = props.viewController;
        this.productReviewsViewController.initialize(props);
    }

    public async componentDidMount() {
        const { reviewsPaginationType, prDisclosureCode } = this.props.config;
        this.productReviewsViewController.setPaginationType(reviewsPaginationType);
        prDisclosureCode && this.productReviewsViewController.setPrDisclosureCode(prDisclosureCode);
        this.productReviewsViewController.showReviews();
    }

    public componentWillUnmount() {
        this.productReviewsViewController.handleProductReviewsUnmount();
    }

    public render() {
        const { reviewsTabLabel, askAnswerTabsLabel } = this.props.translations as ITranslation;
        const { isLoading, activeTab } = this.productReviewsViewController.data;
        const { onTabClick } = this.productReviewsViewController;

        const onKeyPress = (keyPressed: React.KeyboardEvent<HTMLElement>) => {
            handleKeyPress(keyPressed, acceptedKeys, onTabClick);
        };

        const reviewsColumnClassNames = classes('reviews', this.props.config.className);
        const reviewsWrapperClassNames = classes('reviews-wrapper');
        const reviewsTabActiveClassName = classnames({ 'tab-active': activeTab === 'tab-1' });
        const askAnswerTabActiveClassName = classnames({ 'tab-active': activeTab === 'tab-2' });
        const reviewsTabContentClassNames = classnames(
            'elc-spp-reviews',
            'js-spp-reviews',
            'tabs-content tab-1',
            { 'tabs-content-active': activeTab === 'tab-1' }
        );
        const askAnswerTabContentClassNames = classnames(
            'elc-spp-ask-answer',
            'js-spp-ask-answer',
            'tabs-content tab-2',
            { 'tabs-content-active': activeTab === 'tab-2' }
        );

        return (
            <div className={reviewsWrapperClassNames} data-test-id={REVIEWS_WRAPPER}>
                {isLoading ? (
                    <SpinnerContainer isLoading={isLoading} />
                ) : (
                    <>
                        <div className={reviewsColumnClassNames} data-test-id={REVIEWS}>
                            <ul className="tabs-nav">
                                <li className={reviewsTabActiveClassName}>
                                    <div
                                        onClick={onTabClick}
                                        onKeyPress={onKeyPress}
                                        role="button"
                                        tabIndex={0}
                                        data-tab="tab-1"
                                    >
                                        {reviewsTabLabel}
                                    </div>
                                </li>
                                <li className={askAnswerTabActiveClassName}>
                                    <div
                                        onClick={onTabClick}
                                        onKeyPress={onKeyPress}
                                        role="button"
                                        tabIndex={0}
                                        data-tab="tab-2"
                                    >
                                        {askAnswerTabsLabel}
                                    </div>
                                </li>
                            </ul>
                            <TabbedContent className={reviewsTabContentClassNames}>
                                <div id="pr-review-display" />
                            </TabbedContent>
                            <TabbedContent className={askAnswerTabContentClassNames}>
                                <div id="pr-questiondisplay" />
                            </TabbedContent>
                        </div>
                    </>
                )}
            </div>
        );
    }
}
// tslint:disable-next-line:no-default-export
export default ProductReviews;
